import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

interface DriverState {
  userId: string;
  displayName: string;
  position: number;
  lapCompleted: number;
  fuelLevelL: number;
  fuelPct: number;
  lapsRemainingWorst: number | null;
  fuelPerLapAvgL: number | null;
  onPitRoad: boolean;
  isGreen: boolean;
  isCaution: boolean;
  sessionLapsRemainEx: number;
  lastUpdate: number;
}

const L_TO_GAL = 0.264172;
const STALE_MS = 15_000;

/**
 * OBS browser source overlay (XPE-250).
 *
 * Requires authentication — the user must be logged in and a member of the
 * team. In OBS, use a browser source pointed at the same domain where the
 * user is already authenticated (cookies carry over).
 *
 * Usage: Add as OBS browser source with URL:
 *   https://projectxpr.app/stream-overlay?team_id=<UUID>
 *
 * Set width/height to match your stream resolution.
 * Enable "Custom CSS" and add: body { background: transparent !important; }
 */
export default function StreamOverlay() {
  const [params] = useSearchParams();
  const teamId = params.get("team_id");
  const [drivers, setDrivers] = useState<Map<string, DriverState>>(new Map());
  const [authError, setAuthError] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!teamId) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/api/live/feed?team_id=${teamId}`;

    let disposed = false;

    const connect = () => {
      if (disposed) return;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onclose = (e) => {
        if (e.code === 4001 || e.code === 4003) {
          setAuthError(true);
          return;
        }
        if (!disposed) setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "telemetry" && msg.frame) {
            setDrivers((prev) => {
              const next = new Map(prev);
              next.set(msg.userId, {
                userId: msg.userId,
                displayName: msg.displayName || "Driver",
                position: msg.frame.position ?? 0,
                lapCompleted: msg.frame.lapCompleted ?? 0,
                fuelLevelL: msg.frame.fuelLevelL ?? 0,
                fuelPct: msg.frame.fuelPct ?? 0,
                lapsRemainingWorst: msg.frame.lapsRemainingWorst ?? null,
                fuelPerLapAvgL: msg.frame.fuelPerLapAvgL ?? null,
                onPitRoad: msg.frame.onPitRoad ?? false,
                isGreen: msg.frame.isGreen ?? false,
                isCaution: msg.frame.isCaution ?? false,
                sessionLapsRemainEx: msg.frame.sessionLapsRemainEx ?? 0,
                lastUpdate: Date.now(),
              });
              return next;
            });
          }
        } catch { /* ignore */ }
      };
    };

    connect();

    const prune = setInterval(() => {
      const now = Date.now();
      setDrivers((prev) => {
        let changed = false;
        const next = new Map(prev);
        for (const [id] of next) {
          if (now - next.get(id)!.lastUpdate > STALE_MS) {
            next.delete(id);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 5000);

    return () => {
      disposed = true;
      clearInterval(prune);
      wsRef.current?.close();
    };
  }, [teamId]);

  const sorted = Array.from(drivers.values()).sort(
    (a, b) => (a.position || 99) - (b.position || 99),
  );

  if (!teamId) {
    return (
      <div style={styles.root}>
        <div style={styles.error}>Missing team_id parameter</div>
      </div>
    );
  }

  if (authError) {
    return (
      <div style={styles.root}>
        <div style={styles.error}>
          Not authenticated. Log in to projectxpr.app in this browser first.
        </div>
      </div>
    );
  }

  if (sorted.length === 0) return <div style={styles.root} />;

  return (
    <div style={styles.root}>
      <div style={styles.panel}>
        <div style={styles.header}>XPE TEAM</div>
        {sorted.map((d) => {
          const fuelGal = d.fuelLevelL * L_TO_GAL;
          const critical =
            d.lapsRemainingWorst !== null && d.lapsRemainingWorst <= 3;
          return (
            <div key={d.userId} style={styles.row}>
              <span style={styles.pos}>P{d.position || "—"}</span>
              <span style={styles.name}>{d.displayName}</span>
              <span style={styles.fuel}>{fuelGal.toFixed(1)}g</span>
              <span
                style={{
                  ...styles.laps,
                  color: critical ? "var(--flag-red)" : "rgba(232, 238, 248, 0.7)",
                }}
              >
                {d.lapsRemainingWorst !== null
                  ? `${d.lapsRemainingWorst}L`
                  : "—"}
              </span>
              {d.onPitRoad && <span style={styles.pit}>PIT</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "transparent",
    fontFamily:
      "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    color: "#e8eef8",
  },
  panel: {
    position: "absolute",
    bottom: 16,
    left: 16,
    minWidth: 240,
    padding: "10px 14px",
    borderRadius: 10,
    background: "rgba(10, 14, 22, 0.82)",
    border: "1px solid rgba(232, 238, 248, 0.12)",
    boxShadow: "0 4px 18px rgba(0, 0, 0, 0.4)",
  },
  header: {
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "rgba(232, 238, 248, 0.55)",
    marginBottom: 6,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "3px 0",
    fontSize: 13,
    fontFamily: "var(--font-body)",
    fontVariantNumeric: "tabular-nums",
  },
  pos: {
    fontWeight: 700,
    minWidth: 28,
    color: "rgba(232, 238, 248, 0.85)",
  },
  name: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: "system-ui, sans-serif",
    fontSize: 13,
    color: "rgba(232, 238, 248, 0.8)",
  },
  fuel: {
    fontWeight: 600,
    color: "rgba(232, 238, 248, 0.7)",
  },
  laps: {
    fontWeight: 600,
    minWidth: 24,
    textAlign: "right",
  },
  pit: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--flag-yellow)",
  },
  error: {
    color: "var(--flag-red)",
    fontSize: 14,
    padding: 24,
  },
};
